import clsx from 'clsx'
import { FieldError, Label } from 'components/core-v2/atoms'
import { Legend } from 'components/core-v2/atoms/legend'
import { RegisterOptions, useFormContext } from 'react-hook-form'
import { ClassName } from 'types/props'

import { getFieldIds } from './__utils__/getFieldIds'
import classes from './radio-field-set.module.scss'

interface Option {
  label: string
  value: string
}
interface ChildProps extends Option {
  parentName: string
  validators?: RegisterOptions
}
const Child = ({ label, parentName, value }: ChildProps) => {
  const { register } = useFormContext()
  const { describedBy } = getFieldIds(parentName)

  return (
    <Label className={classes.childRoot} htmlFor={value}>
      <input
        {...register(parentName)}
        aria-describedby={describedBy}
        className={classes.childInput}
        id={value}
        name={parentName}
        type="radio"
        value={value}
      />
      {label}
    </Label>
  )
}

export interface RadioFieldProps extends ClassName {
  isDisabled?: boolean
  isLegendInline?: boolean
  legend: React.ReactNode
  name: string
  options: Option[]
  orientation?: 'vertical' | 'horizontal'
  validators?: RegisterOptions
}

export const RadioFieldSet = ({
  className,
  isDisabled = false,
  isLegendInline = false,
  legend,
  name,
  options,
  orientation = 'horizontal',
  validators
}: RadioFieldProps) => {
  const {
    formState: { errors }
  } = useFormContext()

  const error = errors[name]?.message
  const hasError = Boolean(error)
  const { errorId } = getFieldIds(name)

  const body = (
    <>
      <Legend
        className={clsx({
          'mb-0': isLegendInline
        })}
      >
        {legend}
      </Legend>
      <div className={orientation === 'horizontal' ? classes.horizontal : classes.vertical}>
        {options.map(option => (
          <Child parentName={name} {...option} key={option.value} validators={validators} />
        ))}
      </div>
      {hasError && <FieldError id={errorId}>{String(error)}</FieldError>}
    </>
  )

  /* Why are we not using fieldset when the legend needs to be inline?

  As of 20/04/23, both Firefox and Chrome do not support display: flex for fieldset elements.
  https://bugzilla.mozilla.org/show_bug.cgi?id=949476

  */

  return isLegendInline ? (
    <div className={clsx(classes.root, className, classes.inlineLegend)}>{body}</div>
  ) : (
    <fieldset className={clsx(classes.root, className)} disabled={isDisabled}>
      {body}
    </fieldset>
  )
}
